import React, {useEffect, useMemo, useState} from 'react'
import {useLazyQuery, useQuery} from '@apollo/client'
import {Button, Col, Modal, Row, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {
  GET_CONTACT_ATTACHMENTS_API,
  GET_ONE_CONTACT_API,
} from '../../../gql/queries/contactsApiQueries'
import {IResOneContact, MemberPolicySummary, NodeContact} from '../../../types/contactsApi'
import {parseId} from '../../../helpers'
import {SignatureFormsEntriesTimeline} from '../../signatureFormsEntries/components/SignatureFormsEntriesTimeline'
import {GET_ALL_SIGNATURE_FORMS_ENTRIES_API} from '../../../gql/queries/signatureFormsEntriesApiQueries'
import {IResSignatureFormsEntriesAPI} from '../../../types/signatureFormsEntriesApi'
import {GET_ALL_POLICIES_STATUSES} from '../../../gql/queries/policiesQueries'
import {AllPolicyStatusesData} from '../../../types/policies'
import {ContactSummarySide} from './preview/ContactSummarySide'
import {ContactSectionsSide} from './preview/ContactSectionsSide'
import {ContactPersonalTab} from './preview/ContactPersonalTab'
import {ContactPoliciesTab} from './preview/ContactPoliciesTab'
import {ContactDocumentsTab} from './preview/ContactDocumentsTab'
import {
  ContactAttachment,
  ContactAttachmentPolicy,
  ContactDocumentGroup,
  ContactPolicyEntry,
  ContactPolicyGroup,
  ContactProfileSection,
  ContactProfileTab,
  IResContactAttachments,
} from './preview/contactProfileTypes'

const UNTYPED_GROUP_KEY = '__untyped__'
const TITLE_SEP = ' — '

export const ModalPreview: React.FC<{
  idContact: string | null
  onClose: () => void
}> = ({idContact, onClose}) => {
  const intl = useIntl()
  const [infoPreview, setInfoPreview] = useState<NodeContact | null>(null)
  const [tab, setTab] = useState<ContactProfileTab>('personal')

  const {data, loading, error} = useQuery<IResOneContact>(GET_ONE_CONTACT_API, {
    fetchPolicy: 'no-cache',
    variables: {id: parseId(idContact ?? '')},
    skip: !idContact,
  })

  const {data: dataPoliciesStatuses} = useQuery<AllPolicyStatusesData>(GET_ALL_POLICIES_STATUSES, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
  })

  const {
    data: dataEntries,
    loading: loadingEntries,
    refetch: refetchEntries,
  } = useQuery<IResSignatureFormsEntriesAPI>(GET_ALL_SIGNATURE_FORMS_ENTRIES_API, {
    variables: {contact: parseId(idContact ?? '')} as any,
    fetchPolicy: 'network-only',
    skip: !idContact,
  })

  const [
    loadAttachments,
    {data: dataAttachments, loading: loadingAttachments, refetch: refetchAttachments},
  ] = useLazyQuery<IResContactAttachments>(GET_CONTACT_ATTACHMENTS_API, {
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (data?.contactById) {
      setInfoPreview(data.contactById)
    }
  }, [data])

  // Los adjuntos son la consulta más pesada: se cargan al entrar al tab por primera vez.
  useEffect(() => {
    if (tab !== 'documents' || !idContact || dataAttachments || loadingAttachments) return
    loadAttachments({variables: {id: parseId(idContact)}})
  }, [tab, idContact, dataAttachments, loadingAttachments, loadAttachments])

  const policies: ContactPolicyEntry[] = useMemo(() => {
    if (!infoPreview) return []
    const map = new Map<string, {policy: any; members: MemberPolicySummary[]}>()

    const upsert = (member: MemberPolicySummary | null | undefined) => {
      if (!member?.policy) return
      const entry = map.get(member.policy.id) || {policy: member.policy, members: []}
      entry.members.push(member)
      map.set(member.policy.id, entry)
    }

    upsert(infoPreview.memberPolicy)
    infoPreview.members?.edges?.forEach(({node}) => upsert(node))

    // `related` es el miembro con el que ESTE contacto participa en la póliza (rol/parentesco).
    return Array.from(map.values()).map(({policy, members}) => ({
      policy,
      related: members[0] ?? null,
    }))
  }, [infoPreview])

  /** Agrupa por `typePolicy` (ACA, VISION, DENTAL, …); las sin tipo van al final. */
  const policyGroups: ContactPolicyGroup[] = useMemo(() => {
    const otherLabel = intl.formatMessage({
      id: 'contacts.profile.policies.otherType',
      defaultMessage: 'Other',
    })
    const map = new Map<string, ContactPolicyEntry[]>()
    policies.forEach((entry) => {
      const key = entry.policy.typePolicy?.typePolicy?.trim() || UNTYPED_GROUP_KEY
      map.set(key, [...(map.get(key) ?? []), entry])
    })
    return Array.from(map.entries())
      .sort(([a], [b]) => {
        if (a === UNTYPED_GROUP_KEY) return 1
        if (b === UNTYPED_GROUP_KEY) return -1
        return a.localeCompare(b)
      })
      .map(([type, entries]) => ({
        type: type === UNTYPED_GROUP_KEY ? otherLabel : type,
        entries: entries.sort((a, b) =>
          `${b.policy.effectiveDate ?? ''}`.localeCompare(`${a.policy.effectiveDate ?? ''}`)
        ),
      }))
  }, [policies, intl])

  /** Adjuntos de todas las pólizas del contacto, deduplicados por póliza y por documento. */
  const documentGroups: ContactDocumentGroup[] = useMemo(() => {
    const contact = dataAttachments?.contactById
    if (!contact) return []
    const byPolicy = new Map<string, ContactDocumentGroup>()

    const collect = (policy: ContactAttachmentPolicy | null) => {
      if (!policy) return
      const group = byPolicy.get(policy.id) || {policy, documents: [] as ContactAttachment[]}
      const seen = new Set(group.documents.map((doc) => doc.id))
      policy.policyAttachmentsSet?.edges?.forEach(({node}: {node: ContactAttachment}) => {
        if (seen.has(node.id)) return
        seen.add(node.id)
        group.documents.push(node)
      })
      byPolicy.set(policy.id, group)
    }

    collect(contact.memberPolicy?.policy ?? null)
    contact.members?.edges?.forEach(({node}) => collect(node.policy))

    return Array.from(byPolicy.values())
      .map((group) => ({
        ...group,
        documents: group.documents.sort((a, b) => `${b.created}`.localeCompare(`${a.created}`)),
      }))
      .filter((group) => group.documents.length > 0)
  }, [dataAttachments])

  const documentsCount = documentGroups.reduce((acc, group) => acc + group.documents.length, 0)
  const signaturesCount = dataEntries?.allSignatureFormsEntries?.edges?.length ?? 0

  const sections: ContactProfileSection[] = useMemo(
    () => [
      {
        key: 'personal',
        icon: 'person-vcard',
        label: intl.formatMessage({
          id: 'contacts.profile.section.personal',
          defaultMessage: 'Personal Info',
        }),
      },
      {
        key: 'policies',
        icon: 'file-earmark-medical',
        label: intl.formatMessage({
          id: 'contacts.profile.section.policies',
          defaultMessage: 'Policies',
        }),
        count: policies.length,
      },
      {
        key: 'documents',
        icon: 'folder-fill',
        label: intl.formatMessage({
          id: 'contacts.profile.section.documents',
          defaultMessage: 'Documents',
        }),
        count: dataAttachments ? documentsCount : undefined,
      },
      {
        key: 'signatures',
        icon: 'vector-pen',
        label: intl.formatMessage({
          id: 'contacts.profile.section.signatures',
          defaultMessage: 'Signature Forms',
        }),
        count: signaturesCount,
      },
    ],
    [intl, policies.length, dataAttachments, documentsCount, signaturesCount]
  )

  if (!idContact) return null

  const side = infoPreview ? (
    <div className='menu-aside'>
      <div className='kt-widget__content pt-0'>
        <ContactSummarySide contact={infoPreview} policiesCount={policies.length} />
      </div>
      <ContactSectionsSide sections={sections} active={tab} onSelect={setTab} />
    </div>
  ) : null

  return (
    <Modal
      show={true}
      onHide={onClose}
      size='lg'
      backdrop='static'
      keyboard={false}
      dialogClassName='modal-wide-sm modal-preview-contact'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='contacts.profile.title' defaultMessage='Client Profile' />
          {infoPreview?.fullName ? TITLE_SEP + infoPreview.fullName : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{backgroundColor: '#f2f3f8'}}>
        {loading ? (
          <div className='d-flex justify-content-center py-5'>
            <Spinner animation='border' variant='primary' />
          </div>
        ) : error ? (
          <div className='alert alert-danger mb-0'>
            <FormattedMessage
              id='contacts.profile.loadError'
              defaultMessage='The contact information could not be loaded.'
            />
          </div>
        ) : !infoPreview ? null : (
          <Row>
            <Col xs={12} md={4} lg={4} xl={3}>
              {side}
            </Col>
            <Col xs={12} md={8} lg={8} xl={9}>
              {tab === 'personal' && <ContactPersonalTab contact={infoPreview} />}
              {tab === 'policies' && (
                <ContactPoliciesTab
                  groups={policyGroups}
                  policiesStatuses={dataPoliciesStatuses}
                  onNavigate={onClose}
                />
              )}
              {tab === 'documents' && (
                <ContactDocumentsTab
                  groups={documentGroups}
                  loading={loadingAttachments}
                  onRefresh={() =>
                    refetchAttachments
                      ? refetchAttachments()
                      : loadAttachments({variables: {id: parseId(idContact)}})
                  }
                  onNavigate={onClose}
                />
              )}
              {tab === 'signatures' && (
                <div className='policy-form-tabs-container'>
                  <div className='kt-heading kt-heading--md custom-border-brand'>
                    <FormattedMessage
                      id='contacts.profile.signatures.title'
                      defaultMessage='Signature Forms'
                    />
                  </div>
                  <SignatureFormsEntriesTimeline
                    loading={loadingEntries}
                    dataEntriesByLead={dataEntries?.allSignatureFormsEntries}
                    refetch={refetchEntries}
                    hasEmail={Boolean(infoPreview.email && infoPreview.email.trim() !== '')}
                    hasPhone={Boolean(infoPreview.phone && infoPreview.phone.trim() !== '')}
                    compact
                  />
                </div>
              )}
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.close' defaultMessage='Close' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
